import React, { useEffect, useState } from 'react';
import { Card, Heading, Grid, Label, TextTypes, Button, Variants, Tooltip, Positions } from '@sede-enterprise/shell-ds-react-framework';
import { useNavigate, useParams } from 'react-router-dom';
import { Col } from '../../components/flexbox/Flex';
import { ContainerDiv } from '../../components/styledComponent/StyledComponent';
import Loader from '../../components/loader/Loader';
import { useToast } from 'rc-toastr';
import { usePageTitle } from '../../services/UtilService';
import { useTranslation } from 'react-i18next';
import { getData } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import './styles.css';

interface ReportConfig {
    reportName: string;
    reportDescription: string;
    filterCriteria: string;
    filterCriteriaValue: string | null;
    selectedGroup: string;
    selectedColumns: string[];
    isActive: boolean;
}

interface AuditInfo {
    createdBy?: string;
    createdOn?: string;
    modifiedBy?: string;
    modifiedOn?: string;
}

interface MasterDataItem {
    id: number;
    masterType: string;
    fieldName: string;
    displayName: string;
    isAggregator: boolean;
    aggregatorFunction: string;
    dataType: string;
    sortOrder: number;
}

const ViewReport = () => {
    const navigate = useNavigate();
    const { reportId } = useParams<{ reportId: string }>();
    const { toast } = useToast();
    const { t } = useTranslation();
    usePageTitle(t('shellAviationAIMS') + ' | View Report');

    const [isLoadingMasterData, setIsLoadingMasterData] = useState(false);
    const [isLoadingReport, setIsLoadingReport] = useState(false);

    const [filterOptions, setFilterOptions] = useState<MasterDataItem[]>([]);
    const [groupOptions, setGroupOptions] = useState<MasterDataItem[]>([]);
    const [columnOptions, setColumnOptions] = useState<MasterDataItem[]>([]);

    const [reportData, setReportData] = useState<ReportConfig>({
        reportName: '',
        reportDescription: '',
        filterCriteria: '',
        filterCriteriaValue: '',
        selectedGroup: '',
        selectedColumns: [],
        isActive: false
    });
    const [auditInfo, setAuditInfo] = useState<AuditInfo>({});

    const formatDateOnly = (value?: string) => {
        if (!value) return '-';
        const datePart = value.split('T')[0].split(' ')[0];
        return datePart || '-';
    };

    const renderTruncatedField = (value?: string | null, overlayWidth = 360) => {
        const fullText = value && value.trim().length > 0 ? value : '-';
        if (fullText === '-') {
            return <span>-</span>;
        }

        const truncatedText = fullText.length > 75 ? `${fullText.slice(0, 75)}...` : fullText;
        const ellipsisStyle = {
            display: 'inline-block',
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        } as React.CSSProperties;

        return (
            <Tooltip
                placement={Positions.Top}
                overlay={<span style={{ display: 'block', maxWidth: `${overlayWidth}px`, whiteSpace: 'normal' }}>{fullText}</span>}
                trigger={<span style={ellipsisStyle}>{truncatedText}</span>}
            />
        );
    };

    const mapApiReportToView = (apiReport: {
        reportName: string;
        reportDescription: string;
        filterId: number;
        filterCriteriaValue: string;
        selectedGroupId: number;
        selectedColumns: { id: number; columnName: string; displayOrder: number }[];
        isActive: boolean;
    }): ReportConfig => {
        const filter = filterOptions.find((option) => option.id === apiReport.filterId);
        const group = groupOptions.find((option) => option.id === apiReport.selectedGroupId);

        const selectedColumns = (apiReport.selectedColumns || [])
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((column) => {
                const match = columnOptions.find(
                    (option) =>
                        option.displayName === column.columnName || option.fieldName === column.columnName
                );
                return match ? match.displayName : column.columnName;
            });

        return {
            reportName: apiReport.reportName || '-',
            reportDescription: apiReport.reportDescription || '-',
            filterCriteria: filter ? filter.displayName : '-',
            filterCriteriaValue: apiReport.filterCriteriaValue || '-',
            selectedGroup: group ? group.displayName : '-',
            selectedColumns,
            isActive: apiReport.isActive
        };
    };

    useEffect(() => {
        setIsLoadingMasterData(true);
        getData(ApiUrls.reportMasterData)
            .then((masterDataResponse) => {
                const masterData = masterDataResponse.data.data;
                const filters = masterData.filter((item: MasterDataItem) => item.masterType === 'F');
                const groups = masterData.filter((item: MasterDataItem) => item.masterType === 'G');
                const columns = masterData.filter((item: MasterDataItem) => item.masterType === 'C');

                setFilterOptions(filters);
                setGroupOptions(groups);
                setColumnOptions(columns);
                setIsLoadingMasterData(false);
            })
            .catch((error) => {
                console.error('Failed to load master data:', error);
                toast.error('Failed to load master data');
                setIsLoadingMasterData(false);
            });
    }, []);

    useEffect(() => {
        if (!reportId || filterOptions.length === 0) return;

        setIsLoadingReport(true);
        getData(`${ApiUrls.report}/${reportId}`)
            .then((response) => {
                const apiReport = response.data.data;
                setReportData(mapApiReportToView(apiReport));
                setAuditInfo({
                    createdBy: apiReport.createdBy,
                    createdOn: apiReport.createdOn,
                    modifiedBy: apiReport.modifiedBy,
                    modifiedOn: apiReport.modifiedOn
                });
                setIsLoadingReport(false);
            })
            .catch((error) => {
                console.error('Failed to load report:', error);
                toast.error('Failed to load report');
                setIsLoadingReport(false);
                navigate('/report/configure');
            });
    }, [reportId, filterOptions.length]);

    return (
        <ContainerDiv className="report-container">
            <Card className="report-card" style={{ border: '2px solid #d9d9d9', borderRadius: '8px', padding: '24px' }}>
                {(isLoadingMasterData || isLoadingReport) && <Loader />}
                {!(isLoadingMasterData || isLoadingReport) && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <Heading level={2} className="report-heading" style={{ margin: 0, fontWeight: 700 }}>
                                View Report
                            </Heading>
                        </div>

                        <Grid columns={2} gap="4%">
                            <div>
                                <Grid columns="30% 70%" style={{ width: '95%' }}>
                                    <Col size="100"><Label bold type={TextTypes.Label}>Report Name</Label></Col>
                                    <Col size="100"><div>{renderTruncatedField(reportData.reportName, 360)}</div></Col>

                                    <Col size="100"><Label bold type={TextTypes.Label}>Report Description</Label></Col>
                                    <Col size="100"><div>{renderTruncatedField(reportData.reportDescription, 420)}</div></Col>
                                </Grid>
                            </div>

                            <div>
                                <Grid columns="30% 70%" style={{ width: '95%' }}>
                                    <Col size="100"><Label bold type={TextTypes.Label}>Group</Label></Col>
                                    <Col size="100"><div>{reportData.selectedGroup || '-'}</div></Col>

                                    <Col size="100"><Label bold type={TextTypes.Label}>Filter Criteria</Label></Col>
                                    <Col size="100"><div>{reportData.filterCriteria || '-'}</div></Col>

                                    <Col size="100"><Label bold type={TextTypes.Label}>Filter Value</Label></Col>
                                    <Col size="100"><div>{reportData.filterCriteriaValue || '-'}</div></Col>

                                    <Col size="100"><Label bold type={TextTypes.Label}>Is Active</Label></Col>
                                    <Col size="100"><div>{reportData.isActive ? 'Active' : 'Inactive'}</div></Col>
                                </Grid>
                            </div>
                        </Grid>

                        <div style={{ marginTop: '30px', marginBottom: '20px' }}>
                            <hr style={{ border: '0', borderTop: '1px solid #d9d9d9', margin: '0' }} />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <Heading level={3} style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                                Report Columns
                            </Heading>
                        </div>

                        <Grid columns="15% 85%" style={{ width: '97.5%' }}>
                            <Col size="100"><Label bold type={TextTypes.Label}>Columns</Label></Col>
                            <Col size="100">
                                <div>{reportData.selectedColumns.length > 0 ? reportData.selectedColumns.join(', ') : '-'}</div>
                            </Col>
                        </Grid>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '40px' }}>
                            <div style={{ fontSize: '14px', color: '#6c757d' }}>
                                <span>
                                    <strong>Modified By:</strong> {auditInfo.modifiedBy || auditInfo.createdBy || '-'}
                                </span>
                                <span style={{ marginLeft: '16px' }}>
                                    <strong>Modified On:</strong> {formatDateOnly(auditInfo.modifiedOn || auditInfo.createdOn)}
                                </span>
                            </div>
                            <Button variant={Variants.Outlined} onClick={() => navigate('/report/configure')}>
                                BACK
                            </Button>
                        </div>
                    </>
                )}
            </Card>
        </ContainerDiv>
    );
};

export default ViewReport;
